import React from 'react';
import { useTranslation } from 'react-i18next';
import { FaUserCircle } from 'react-icons/fa';
import { useRecoilValue } from 'recoil';
import { COLOURS } from '@tq/ui-components/dist/pp2/theme';
import {
  Button,
  Grid,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useBanner,
} from '@tq/ui-components';
import { tqc } from '@tq/ui-components/dist/system';

import { useInviteConsultee } from 'api';
import { MobileModal } from 'components';
import { BANNER_TIMEOUT } from 'config';
import { useIsMobile } from '@tq/ui-components/dist/hooks';
import {
  inviteNameState,
  inviteEmailState,
  inviteRequestState,
  useResetInviteUserState,
} from 'store';

type ManageTeamModalProps = {
  onClose: () => void;
};

export const InviteUserModal: React.FC<ManageTeamModalProps> = ({ onClose }) => {
  const { t } = useTranslation('manage_team_modal');
  const isMobile = useIsMobile();
  const { mutate: inviteConsultee } = useInviteConsultee();

  const inviteName = useRecoilValue(inviteNameState);
  const inviteEmail = useRecoilValue(inviteEmailState);
  const inviteRequest = useRecoilValue(inviteRequestState);
  const resetInviteState = useResetInviteUserState();

  const banner = useBanner();
  const handleInvite = () => {
    inviteConsultee(inviteRequest, {
      onSuccess: () => {
        resetInviteState();
        onClose();
        banner({
          id: 'manage-team-banner',
          title: t('invite_submission_success', 'User invited'),
          duration: BANNER_TIMEOUT,
        });
      },
      onError: () => {
        banner({
          id: 'manage-team-banner-fail',
          title: t('invite_submission_failed', 'Failed to send invitation. Please try again.'),
          duration: BANNER_TIMEOUT,
          status: 'error',
        });
      },
    });
  };

  const content = (
    <>
      <ModalHeader>
        <Grid gridAutoFlow='column' gridGap={4}>
          <FaUserCircle color={COLOURS.blue[600]} />
          <span>{t('invite_user_heading', 'Invite user')}</span>
        </Grid>
        <ModalCloseButton />
      </ModalHeader>
      <ModalBody aria-label='Invite user modal body' overflow='visible'>
        <tqc.p mb='1rem'>
          {t('invite_user_body', 'You are about to invite the following user:')}
        </tqc.p>
        <span>{inviteName + ' <' + inviteEmail + '>'}</span>
      </ModalBody>
    </>
  );

  return isMobile ? (
    <MobileModal
      aria-label={t('invite-user-modal-content-label', 'Invite user prompt')}
      isOpen
      onClose={onClose}
    >
      {content}
      <ModalFooter>
        <Grid gridGap={4} w='100%'>
          <Button isFullWidth type='submit' onClick={handleInvite}>
            {t('invite_user_text', 'Invite')}
          </Button>
          <Button isFullWidth variant='tertiary' onClick={onClose}>
            {t('cancel_text', 'Cancel')}
          </Button>
        </Grid>
      </ModalFooter>
    </MobileModal>
  ) : (
    <Modal isOpen onClose={onClose} data-testid='invite-user-modal'>
      <ModalOverlay />
      <ModalContent
        data-testid='invite-user-modal-content'
        aria-label={t('invite-user-modal-content-label', 'Invite user prompt')}
      >
        {content}
        <ModalFooter justifyContent='end'>
          <Grid gridAutoFlow='column' gridGap={4}>
            <Button variant='tertiary' onClick={onClose}>
              {t('cancel_text', 'Cancel')}
            </Button>
            <Button type='submit' onClick={handleInvite}>
              {t('invite_user_text', 'Invite')}
            </Button>
          </Grid>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
